import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, theme, message, Table } from 'antd';
import { HomeOutlined, TeamOutlined, FileOutlined, DollarOutlined } from '@ant-design/icons';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Line,
  Bar
} from 'recharts';
import { REPORT_API } from '../constants/api';

function Dashboard() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { token } = theme.useToken();
  const [dashboardStats, setDashboardStats] = useState({
    totalFactories: 0,
    rentedFactories: 0,
    activeContracts: 0,
    currentMonthRevenue: 0,
    totalPaidRevenue: 0,
  });
  const [revenueData, setRevenueData] = useState([]);
  const [revenueTableData, setRevenueTableData] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const data = await getDashboardStats();
        setDashboardStats(data);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      }
    };

    const fetchRevenueData = async () => {
      try {
        const data = await getMonthlyRevenue();
        console.log(data);
        setRevenueData(data);
        // Xử lý dữ liệu cho bảng thống kê
        const tableData = data.map((item, index) => {
          let comparisonValue = 0;
          if (index > 0 && data[index - 1].rentRevenue !== 0) {
            comparisonValue = ((item.rentRevenue - data[index - 1].rentRevenue) / data[index - 1].rentRevenue * 100);
          } else if (index > 0 && data[index - 1].rentRevenue === 0 && item.rentRevenue > 0) {
            comparisonValue = 100; // Tăng 100% khi từ 0 lên có doanh thu
          }
          return {
            key: index,
            month: `Tháng ${item.month}`,
            revenue: item.rentRevenue || 0,
            totalRevenue: item.totalRevenue || 0,
            utilityRevenue: (item.electricityRevenue || 0) + (item.waterRevenue || 0) + (item.wasteWaterRevenue || 0),
            electricityRevenue: item.electricityRevenue || 0,
            waterRevenue: item.waterRevenue || 0,
            wasteWaterRevenue: item.wasteWaterRevenue || 0,
            contracts: item.contractCount || 0,
            comparison: index === 0 ? null : comparisonValue.toFixed(2)
          };
        });
        console.log(tableData);
        setRevenueTableData(tableData);
      } catch (error) {
        console.error('Error fetching revenue data:', error);
        message.error('Không thể tải dữ liệu doanh thu');
      }
    };

    fetchDashboardData();
    fetchRevenueData();
  }, []);

  // Calculate occupancy data from dashboard stats
  const occupancyData = [
    { name: 'Đã cho thuê', value: dashboardStats.rentedFactories },
    { name: 'Còn trống', value: Math.max(0, dashboardStats.totalFactories - dashboardStats.rentedFactories) },
  ];

  const COLORS = ['#52c41a', 'orange'];

  const handleResize = useCallback(() => {
    let timeoutId = null;
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 200);
    };
  }, []);

  useEffect(() => {
    const resizeListener = handleResize();
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [handleResize]);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  const formatRevenue = (value) => {
    return `${(value / 1000000).toFixed(0)}tr`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ margin: 0 }}>{`Tháng ${label}`}</p>
          <p style={{ margin: 0, color: '#1890ff' }}>
            {`Doanh thu thuê: ${payload[0]?.value?.toLocaleString('vi-VN')} VNĐ`}
          </p>
          <p style={{ margin: 0, color: '#52c41a' }}>
            {`Doanh thu tiện ích: ${payload[1]?.value?.toLocaleString('vi-VN')} VNĐ`}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ margin: 0, color: payload[0].payload.fill }}>
            {`${payload[0].name}: ${payload[0].value} nhà xưởng`}
          </p>
        </div>
      );
    }
    return null;
  };

  const getDashboardStats = async () => {
    try {
      const response = await fetch(REPORT_API.DASHBOARD, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      const stats = data.data || {};

      const activeContracts = stats.activeContracts !== undefined
        ? stats.activeContracts
        : (stats.contractStatus
            ? stats.contractStatus
                .filter(c => ['active', 'expiring'].includes(String(c.Status || '').toLowerCase()))
                .reduce((sum, c) => sum + Number(c.count || 0), 0)
            : (stats.occupiedFactories || 0));

      const rentedFactories = stats.rentedFactories !== undefined
        ? stats.rentedFactories
        : (stats.occupiedFactories !== undefined ? stats.occupiedFactories : activeContracts);

      const totalFactories = stats.totalFactories !== undefined ? stats.totalFactories : 0;

      return {
        totalFactories: totalFactories,
        rentedFactories: rentedFactories,
        activeContracts: activeContracts,
        currentMonthRevenue: stats.rentStats?.totalAmount || 0,
        totalPaidRevenue: stats.rentStats?.paidAmount || 0
      };
    } catch (error) {
      console.error('Error in getDashboardStats:', error);
      message.error('Không thể tải dữ liệu tổng quan');
      return {
        totalFactories: 0,
        rentedFactories: 0,
        activeContracts: 0,
        currentMonthRevenue: 0,
        totalPaidRevenue: 0
      };
    }
  };

  const getMonthlyRevenue = async () => {
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0];

      const response = await fetch(`${REPORT_API.REVENUE}?startDate=${startDate}&endDate=${endDate}&groupBy=month`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      console.log(data.data?.revenue);

      // Transform the data to match the expected format for the chart
      if (data.data && data.data.revenue) {
        return data.data.revenue.map(item => {
          // Parse "YYYYMM" to month string
          const year = item.period.substring(0, 4);
          const month = item.period.substring(4, 6);
          return {
            month: parseInt(month, 10).toString(),
            monthName: `Tháng ${month}/${year}`,
            rentRevenue: item.rentRevenue || 0,
            contractCount: item.contracts || item.contractCount || 0,
            electricityRevenue: item.electricityRevenue || 0,
            waterRevenue: item.waterRevenue || 0,
            wasteWaterRevenue: item.wasteWaterRevenue || 0,
            utilityRevenue: item.utilityRevenue || 0,
            totalRevenue: item.totalRevenue || 0,
          };
        }).sort((a, b) => {
          // Sort chronologically using monthName which has YYYY and MM
          const [mA, yA] = a.monthName.replace('Tháng ', '').split('/');
          const [mB, yB] = b.monthName.replace('Tháng ', '').split('/');
          if (yA !== yB) return yA - yB;
          return mA - mB;
        });
      }

      return [];
    } catch (error) {
      console.error('Error in getMonthlyRevenue:', error);
      message.error('Không thể tải dữ liệu doanh thu');
      return [];
    }
  };

  // Thêm cấu hình cột cho bảng
  const columns = [
    {
      title: 'Thời gian',
      dataIndex: 'month',
      key: 'month',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        const monthA = parseInt(a.month.replace('Tháng ', ''));
        const monthB = parseInt(b.month.replace('Tháng ', ''));
        return monthB - monthA;
      },
    },
    {
      title: 'Doanh thu thuê (VNĐ)',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (value) => value.toLocaleString('vi-VN'),
      sorter: (a, b) => a.revenue - b.revenue,
    },
    {
      title: 'Tiền điện (VNĐ)',
      dataIndex: 'electricityRevenue',
      key: 'electricityRevenue',
      render: (value) => value.toLocaleString('vi-VN'),
      sorter: (a, b) => a.electricityRevenue - b.electricityRevenue,
    },
    {
      title: 'Tiền nước sạch (VNĐ)',
      dataIndex: 'waterRevenue',
      key: 'waterRevenue',
      render: (value) => value.toLocaleString('vi-VN'),
      sorter: (a, b) => a.waterRevenue - b.waterRevenue,
    },
    {
      title: 'Tiền nước thải (VNĐ)',
      dataIndex: 'wasteWaterRevenue',
      key: 'wasteWaterRevenue',
      render: (value) => (value || 0).toLocaleString('vi-VN'),
      sorter: (a, b) => (a.wasteWaterRevenue || 0) - (b.wasteWaterRevenue || 0),
    },
    {
      title: 'Doanh thu tiện ích (VNĐ)',
      dataIndex: 'utilityRevenue',
      key: 'utilityRevenue',
      render: (value) => value.toLocaleString('vi-VN'),
      sorter: (a, b) => a.utilityRevenue - b.utilityRevenue,
    },
    {
      title: 'Tổng doanh thu (VNĐ)',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (value) => value.toLocaleString('vi-VN'),
      sorter: (a, b) => a.totalRevenue - b.totalRevenue,
    },
    {
      title: 'Số hợp đồng',
      dataIndex: 'contracts',
      key: 'contracts',
      sorter: (a, b) => a.contracts - b.contracts,
    },
    {
      title: 'So với tháng trước (%)',
      dataIndex: 'comparison',
      key: 'comparison',
      render: (value) => {
        if (value === null) return '-';
        const numValue = parseFloat(value);
        const color = numValue > 0 ? '#52c41a' : numValue < 0 ? '#f5222d' : '#faad14';
        return (
          <span style={{ color }}>
            {numValue > 0 ? '+' : ''}{numValue}%
          </span>
        );
      },
    },
  ];

  return (
    <div style={{
      padding: '16px',
      backgroundColor: token.colorBgContainer
    }}>
      <Card
        title={<h2 style={{ margin: 0 }}>Tổng Quan Doanh Thu 2026</h2>}
        bordered={false}
        bodyStyle={{ padding: isMobile ? 8 : 16 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable bodyStyle={{ padding: '24px 16px' }}>
              <Statistic
                title="Tổng số nhà xưởng"
                value={dashboardStats.totalFactories}
                prefix={<HomeOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable bodyStyle={{ padding: '24px 16px' }}>
              <Statistic
                title="Số nhà xưởng đã cho thuê"
                value={dashboardStats.rentedFactories}
                prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable bodyStyle={{ padding: '24px 16px' }}>
              <Statistic
                title="Hợp đồng đang có hiệu lực"
                value={dashboardStats.activeContracts}
                prefix={<FileOutlined style={{ color: '#722ed1' }} />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable bodyStyle={{ padding: '24px 16px' }}>
              <Statistic
                title="Tổng doanh thu"
                value={revenueData.reduce((total, row) => total + row.totalRevenue, 0)}
                prefix={<DollarOutlined style={{ color: '#fa8c16' }} />}
                suffix="VNĐ"
                valueStyle={{ color: '#fa8c16' }}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={16}>
            <Card
              title="Biểu đồ lợi nhuận theo tháng"
              bordered={false}
              bodyStyle={{ height: isMobile ? 300 : 400, padding: '20px 0' }}
            >
              <ResponsiveContainer width="99%" height="99%">
                <BarChart
                  data={revenueData}
                  margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 20,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    label={{ value: 'Tháng', position: 'bottom' }}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    yAxisId="left"
                    tickFormatter={formatRevenue}
                    domain={[0, dataMax => Math.ceil(dataMax * 1.2)]}
                    label={{ value: 'Doanh thu', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" height={36} />
                  <Bar
                    yAxisId="left"
                    dataKey="rentRevenue"
                    name="Doanh thu thuê"
                    fill="#1890ff"
                    barSize={20}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="utilityRevenue"
                    name="Doanh thu tiện ích"
                    fill="#52c41a"
                    barSize={20}
                    radius={[4, 4, 0, 0]}
                  />

                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              title="Tỷ lệ thuê nhà xưởng"
              bordered={false}
              bodyStyle={{
                height: isMobile ? 300 : 400,
                padding: '20px 0'
              }}
            >
              <ResponsiveContainer width="99%" height="99%">
                <PieChart>
                  <Pie
                    data={occupancyData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#1890ff"
                    label={({ name, value }) => `${name}: ${value}`}

                    dataKey="value"
                  >
                    {occupancyData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card
              title="Thống kê doanh thu theo tháng"
              bordered={false}
            >
              <Table
                columns={columns}
                dataSource={revenueTableData}
                pagination={false}
                scroll={{ x: true }}
                summary={pageData => {
                  const totalRevenue = pageData.reduce((total, row) => total + row.revenue, 0);
                  const totalUtilityRevenue = pageData.reduce((total, row) => total + row.utilityRevenue, 0);
                  const totalElectricityRevenue = pageData.reduce((total, row) => total + row.electricityRevenue, 0);
                  const totalWaterRevenue = pageData.reduce((total, row) => total + row.waterRevenue, 0);
                  const totalWasteWaterRevenue = pageData.reduce((total, row) => total + row.wasteWaterRevenue, 0);
                  const totalTotalRevenue = pageData.reduce((total, row) => total + row.totalRevenue, 0);
                  const totalContracts = pageData.reduce((total, row) => total + row.contracts, 0);

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>Tổng cộng</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>{totalRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>{totalElectricityRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>{totalWaterRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>{totalWasteWaterRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>{totalUtilityRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>{totalTotalRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                        <Table.Summary.Cell index={7}>{totalContracts}</Table.Summary.Cell>
                        <Table.Summary.Cell index={8}>-</Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
}

export default Dashboard;
